import { useMemo } from "react";
import type { OfferFormDataT } from "@/app/types/offers";
import type { VehicleDetailT } from "@/app/components/ui/vehicle-details-list";
import {
  formatNumberWithUnit,
  formatPrice,
} from "@/app/components/offers/utils/formatters";
import { TRUCK_FUEL_TYPE_OPTIONS } from "@/app/utils/constants/backend/offers/select-options";
import { getTotalMonthlyLeasingRate } from "@/app/utils/helpers/backend/offers/get-total-monthly-leasing-rate";
import { formatDate as formatVehicleDate } from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen-old/lib/vehicles-formatter";

/**
 * Get localized label for gearbox type
 */
const getGearboxLabel = (gearbox: string): string => {
  switch (gearbox) {
    case "automatic":
      return "Automatikgetriebe";
    case "manual":
      return "Schaltgetriebe";
    default:
      return gearbox;
  }
};

/**
 * Get localized label for fuel type
 */
const getFuelLabel = (fuelType: string): string => {
  switch (fuelType) {
    case "gasoline":
      return "Benzin";
    case "diesel":
      return "Diesel";
    case "electric":
      return "Elektro";
    case "hybrid":
      return "Hybrid-Benzin";
    default:
      return fuelType;
  }
};

/**
 * Get display label for the fuel type selected for truck offers
 * Returns null when no selection was made (row is hidden then)
 */
const getTruckFuelLabel = (
  truckFuelType: string | null | undefined
): string | null =>
  TRUCK_FUEL_TYPE_OPTIONS.find((option) => option.value === truckFuelType)
    ?.label ?? null;

/**
 * Format power from kW to "kW (PS)" format
 * Returns null if power is not available
 */
const formatPower = (power: number | null | undefined): string | null => {
  if (power === null || power === undefined) {
    return null;
  }
  const ps = Math.round(power * 1.36); // Convert kW to PS
  return `${power}kW (${ps} PS)`;
};

/**
 * Format duration in months
 * Returns null if duration is not available
 */
const formatDuration = (duration: number | null | undefined): string | null => {
  if (duration === null || duration === undefined) {
    return null;
  }
  return `${duration} Monate`;
};

/**
 * Hook to extract and format vehicle details for VehicleDetailsList component
 */
export const useOfferVehicleDetails = (
  offer: OfferFormDataT
): VehicleDetailT[] => {
  return useMemo(() => {
    const pricingType = offer.pricing?.type;
    const isUsedOffer = offer.vehicle_condition === "used";

    // Annual mileage is only relevant for leasing.
    const annualMileage =
      pricingType === "leasing"
        ? (offer.pricing?.leasing?.jaehrlicheFahrleistung ?? null)
        : null;

    // Determine duration based on pricing type
    let duration: number | null | undefined = null;
    if (pricingType === "leasing") {
      duration = offer.pricing?.leasing?.vertragsdauer;
    } else if (pricingType === "finanzierung") {
      duration = offer.pricing?.finanzierung?.vertragsdauer;
    }
    // Barkauf has no duration

    // Determine down payment based on pricing type
    let downPayment: number | null | undefined = null;
    if (pricingType === "leasing") {
      downPayment = offer.pricing?.leasing?.sonderzahlung;
    } else if (pricingType === "finanzierung") {
      downPayment = offer.pricing?.finanzierung?.anzahlung;
    } else if (pricingType === "barkauf") {
      downPayment = offer.pricing?.barkauf?.anzahlung;
    }

    // Determine rate based on pricing type
    let rate: number | null | undefined = null;
    let rateType: "leasingRate" | "finanzierungsRate" | null = null;
    if (pricingType === "leasing") {
      rate = getTotalMonthlyLeasingRate(offer);
      rateType = "leasingRate";
    } else if (pricingType === "finanzierung") {
      rate = offer.pricing?.finanzierung?.mtlRate;
      rateType = "finanzierungsRate";
    }
    // Barkauf has no rate

    // Determine purchase price for Barkauf
    let purchasePrice: number | null | undefined = null;
    if (pricingType === "barkauf") {
      purchasePrice = offer.pricing?.barkauf?.anschaffungspreisBrutto;
    }

    const details: VehicleDetailT[] = [];

    // Add color if available
    if (offer.lackierung?.trim()) {
      details.push({ type: "color", value: offer.lackierung });
    }

    // Add interior color if available
    if (offer.ausstattungsfarbe?.trim()) {
      details.push({
        type: "interiorColor",
        value: offer.ausstattungsfarbe,
      });
    }

    // Add gearbox if available
    if (offer.getriebe) {
      details.push({
        type: "gearbox",
        value: getGearboxLabel(offer.getriebe),
      });
    }

    // Add fuel type if available
    // Trucks have no consumption data; show the backend-selected display label
    if (offer.consumption?.type === "truck") {
      const truckFuelLabel = getTruckFuelLabel(offer.consumption.truckFuelType);
      if (truckFuelLabel) {
        details.push({ type: "fuel", value: truckFuelLabel });
      }
    } else if (offer.consumption?.type) {
      details.push({
        type: "fuel",
        value: getFuelLabel(offer.consumption.type),
      });
    }

    // Add power if available
    const powerValue = formatPower(offer.leistung);
    if (powerValue) {
      details.push({
        type: "power",
        value: powerValue,
      });
    }

    const rawKilometers = offer.kilometerlaufleistung as unknown;
    const normalizedKilometers =
      typeof rawKilometers === "number"
        ? rawKilometers
        : typeof rawKilometers === "string"
          ? Number.parseFloat(rawKilometers)
          : null;
    const totalMileageValue =
      isUsedOffer && Number.isFinite(normalizedKilometers)
        ? formatNumberWithUnit(normalizedKilometers, "km", 0)
        : null;

    if (totalMileageValue) {
      details.push({
        type: "totalMileage",
        value: totalMileageValue,
      });
    }

    const firstRegistrationValue =
      isUsedOffer && offer.erstzulassung
        ? formatVehicleDate(offer.erstzulassung)
        : null;

    if (firstRegistrationValue) {
      details.push({
        type: "firstRegistration",
        value: firstRegistrationValue,
      });
    }

    if (isUsedOffer && typeof offer.vorbesitzer === "number") {
      details.push({
        type: "previousOwners",
        value: offer.vorbesitzer.toString(),
      });
    }

    // Add annual mileage only for leasing if available
    const mileageValue = formatNumberWithUnit(annualMileage, "km", 0);
    if (mileageValue) {
      details.push({
        type: "mileage",
        value: mileageValue,
      });
    }

    // Add duration if available
    const durationValue = formatDuration(duration);
    if (durationValue) {
      details.push({
        type: "duration",
        value: durationValue,
      });
    }

    // Add down payment if available
    const downPaymentValue = formatPrice(downPayment);
    if (downPaymentValue) {
      details.push({
        type: pricingType === "leasing" ? "specialPayment" : "downPayment",
        value: downPaymentValue,
      });
    }

    // Add transfer cost if available
    const transferCostValue = formatPrice(offer.pricing?.ueberfuehrungskosten);
    if (transferCostValue) {
      details.push({
        type: "transferCost",
        value: transferCostValue,
      });
    }

    // Add rate if available
    if (rate && rateType) {
      const rateValue = formatPrice(rate);
      if (rateValue) {
        details.push({
          type: rateType,
          value: rateValue,
        });
      }
    }

    // Add purchase price if available
    if (purchasePrice) {
      const purchasePriceValue = formatPrice(purchasePrice);
      if (purchasePriceValue) {
        details.push({
          type: "anschaffungspreis",
          value: purchasePriceValue,
        });
      }
    }

    // Add maintenance and inspection costs if available (only for leasing)
    if (pricingType === "leasing") {
      const maintenanceInspectionCosts =
        offer.pricing?.leasing?.wartungInspektionskosten;
      if (
        maintenanceInspectionCosts != null &&
        maintenanceInspectionCosts > 0
      ) {
        const maintenanceValue = formatPrice(maintenanceInspectionCosts);
        if (maintenanceValue) {
          details.push({
            type: "maintenanceInspection",
            value: maintenanceValue,
          });
        }
      }
    }

    return details;
  }, [
    offer.lackierung,
    offer.ausstattungsfarbe,
    offer.getriebe,
    offer.consumption?.type,
    offer.leistung,
    offer.pricing?.type,
    offer.pricing?.leasing?.jaehrlicheFahrleistung,
    offer.pricing?.leasing?.vertragsdauer,
    offer.pricing?.leasing?.sonderzahlung,
    offer.pricing?.leasing?.mtlLeasingrate,
    offer.pricing?.finanzierung?.vertragsdauer,
    offer.pricing?.finanzierung?.anzahlung,
    offer.pricing?.finanzierung?.mtlRate,
    offer.pricing?.barkauf?.anzahlung,
    offer.pricing?.barkauf?.anschaffungspreisBrutto,
    offer.kilometerlaufleistung,
    offer.vorbesitzer,
    offer.erstzulassung,
    offer.pricing?.ueberfuehrungskosten,
    offer.pricing?.leasing?.wartungInspektionskosten,
    offer.vehicle_condition,
  ]);
};
